import React, { FC, useCallback } from 'react';
import {
  ActionButton,
  DataObjectVersionOptionBadge,
  DataObjectVersionOptionLabel,
  DataObjectVersionSelect,
  formatDateTimeShort,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  OutputSnapshotVersionType,
  OutputVersionType,
  Padding,
  RequiredText,
} from '@atlas-frontend/sdk';
import { Flex, Select } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './OutputVersionSelectionModal.translations.json';
import { useOutputDescription } from './useOutputDescription';
import { useOutputValueDates } from './useOutputValueDates';
import { useOutputVersions } from './useOutputVersions';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-version-selection-modal',
);
addTranslations(translations);

export interface OutputVersionSelectionModalProps {
  descriptionId?: string;
  onClose: () => void;
  onSelect: (snapshot: OutputSnapshotVersionType) => void;
  subject: string;
  valueDate: string;
}

export const OutputVersionSelectionModal: FC<
  OutputVersionSelectionModalProps
> = ({ onClose, onSelect, ...params }) => {
  const { t } = useTranslation();
  const { isLoading, options, output, setOutput } = useOutputDescription(
    params.valueDate,
    params.descriptionId,
  );
  const {
    isLoading: datesLoading,
    options: dateOptions,
    outputValueDate,
    setOutputValueDate,
  } = useOutputValueDates(
    output?.outputDescriptionId,
    params.descriptionId,
    params.valueDate,
  );
  const {
    isLoading: versionsLoading,
    data: versionsData,
    outputVersion,
    setOutputVersion,
  } = useOutputVersions(
    outputValueDate?.resultDoId,
    params.subject,
    params.descriptionId,
    params.valueDate,
  );

  const onAccept = useCallback(() => {
    if (output && outputValueDate && outputVersion) {
      onSelect({ output, outputDate: outputValueDate, outputVersion });
    }
  }, [onSelect, output, outputValueDate, outputVersion]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              disabled={!outputVersion}
              label={t('button.ok')}
              onClick={onAccept}
            />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t('heading.label')}</ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <Flex vertical gap="small">
          <label htmlFor="output-select">
            <RequiredText required={true}>{t('field.output')}</RequiredText>
          </label>
          <Select
            id="output-select"
            loading={isLoading}
            onSelect={(_, option) => setOutput(option.output)}
            optionFilterProp="label"
            options={options}
            showSearch
            value={output?.outputDescriptionId}
          />
          <label htmlFor="output-date-select">
            <RequiredText required={true}>{t('field.outputDate')}</RequiredText>
          </label>
          <Select
            id="output-date-select"
            loading={datesLoading}
            onSelect={(_, option) => setOutputValueDate(option.outputDate)}
            options={dateOptions}
            value={outputValueDate?.resultDoId}
            style={{ maxWidth: '180px' }}
          />
          <label htmlFor="output-version-select">
            <RequiredText required={true}>
              {t('field.outputVersion')}
            </RequiredText>
          </label>
          <DataObjectVersionSelect<OutputVersionType>
            versions={versionsData ?? []}
            activeVersionId={outputVersion?.outputDovId}
            wrapperHeight="auto"
            style={{ maxWidth: '240px' }}
            onChange={(value) => {
              const selected = versionsData?.find(
                (v) => v.outputDovId === value,
              );
              if (selected?.inputSources.length) {
                setOutputVersion(selected);
              }
            }}
            renderOption={(version) => ({
              label: (
                <DataObjectVersionOptionLabel
                  gap="small"
                  className={version.outputStatusCode || 'DRAFT'}
                >
                  {(version.outputVersionNumber ||
                    version.outputMinorNumber) && (
                    <>
                      <DataObjectVersionOptionBadge
                        count={`${version.outputVersionNumber}${
                          version.outputMinorNumber
                            ? `.${version.outputMinorNumber}`
                            : ''
                        }`}
                        showZero
                        color="black"
                        size="small"
                      />{' '}
                    </>
                  )}
                  {formatDateTimeShort(version.calculationTime)}{' '}
                  {t(`status.${version.outputStatusCode ?? 'DRAFT'}`)}
                </DataObjectVersionOptionLabel>
              ),
              value: version.outputDovId,
              className: version.outputStatusCode || 'DRAFT',
              disabled: !version.active || !version.inputSources.length,
            })}
            disabled={versionsLoading}
          />
        </Flex>
      </Padding>
    </Modal>
  );
};
